import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { type RegionDetail, getRegion } from "../source-explorer/explorerClient";
import {
  type FieldResolution,
  type LifecycleMove,
  type RecipeField,
  type RecipePreview,
  type RecipeVersion,
  type RegressionReport,
  addTestCase,
  approveVersion,
  getPreview,
  getVersion,
  message,
  moveVersion,
  rollbackTo,
  runRegression,
  saveVersion,
} from "./recipesClient";

const OWNER_UNASSIGNED = "unassigned";

function lines(form: FormData, name: string): string[] {
  return String(form.get(name) ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Which lifecycle moves make sense from a state. The server still decides. */
const MOVES: Record<string, { move: LifecycleMove; label: string }[]> = {
  approved: [
    { move: "activate", label: "Activate" },
    { move: "retire", label: "Retire" },
  ],
  active: [
    { move: "suspend", label: "Suspend" },
    { move: "retire", label: "Retire" },
  ],
  suspended: [
    { move: "resume", label: "Resume" },
    { move: "retire", label: "Retire" },
  ],
  proposed: [{ move: "retire", label: "Retire" }],
  under_evaluation: [{ move: "retire", label: "Retire" }],
  superseded: [{ move: "retire", label: "Retire" }],
};

/** What the region says, read through the source explorer so its preview policy applies. */
function RegionValue({ resolution }: { resolution: FieldResolution }) {
  const { parsed_document_id: documentId, resolved } = resolution;
  const [region, setRegion] = useState<RegionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId || !resolved) return undefined;
    let live = true;
    getRegion(documentId, resolved.region_key)
      .then((found) => {
        if (live) setRegion(found);
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [documentId, resolved]);

  if (!resolved) return <span>Not found in this file</span>;
  if (error) return <span role="alert">Could not read the region: {error}</span>;
  if (!region) return <span>Reading {resolved.region_key}</span>;
  const shown = region.parsed.withheld
    ? "Withheld by this file's preview policy"
    : (region.parsed.displayed_value ?? region.parsed.text_content ?? "(empty)");
  return (
    <span>
      {shown} <small>({resolved.region_key}, matched by {resolved.match})</small>
    </span>
  );
}

function PreviewCell({ preview, name }: { preview: RecipePreview | null; name: string }) {
  const field = preview?.fields.find((item) => item.field_name === name);
  if (!field) return <span>No preview yet</span>;
  return (
    <ul>
      {field.resolutions.map((resolution) => (
        <li key={resolution.source_artifact_version_id}>
          {resolution.representative_role}: <RegionValue resolution={resolution} />
        </li>
      ))}
    </ul>
  );
}

/** One recipe version: fields with their anchors resolved on the representatives, test cases, and
 * approval. Saving records a new version, so an approved version never changes. */
export function RecipeEditorPage() {
  const { versionId = "" } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<RecipeVersion | null>(null);
  const [preview, setPreview] = useState<RecipePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [regression, setRegression] = useState<RegressionReport | null>(null);
  const [moveReason, setMoveReason] = useState("");

  const load = useCallback(() => {
    Promise.all([getVersion(versionId), getPreview(versionId)])
      .then(([found, shown]) => {
        setRecipe(found);
        setPreview(shown);
        setError(null);
      })
      .catch((caught: unknown) => setError(message(caught)));
  }, [versionId]);

  useEffect(load, [load]);

  function fail(caught: unknown) {
    setStatus(null);
    setError(message(caught));
  }

  function onSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!recipe) return;
    const form = new FormData(event.currentTarget);
    const fields: RecipeField[] = recipe.fields.map((field, index) => ({
      ...field,
      meaning: String(form.get(`meaning-${index}`) ?? "").trim() || null,
      source_anchor_pattern: JSON.parse(String(form.get(`anchor-${index}`))) as Record<string, unknown>,
    }));
    saveVersion({
      family_id: recipe.family_id,
      fields,
      owner_party: String(form.get("owner") ?? "").trim(),
      governance: {
        known_exclusions: lines(form, "exclusions"),
        proof_limits: lines(form, "limits"),
        revalidation_triggers: lines(form, "triggers"),
        rollback_path: String(form.get("rollback") ?? "").trim() || null,
      },
      reason: String(form.get("reason") ?? "").trim(),
    })
      .then((saved) => navigate(`/recipes/${saved.id}`))
      .catch(fail);
  }

  function onCase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    addTestCase(versionId, {
      case_kind: String(form.get("kind")) as "clean" | "edge" | "negative",
      description: String(form.get("description") ?? "").trim(),
      input: JSON.parse(String(form.get("input") || "{}")) as unknown,
      expected: JSON.parse(String(form.get("expected") || "{}")) as unknown,
    })
      .then((updated) => {
        setRecipe(updated);
        setStatus("Test case added.");
        setError(null);
      })
      .catch(fail);
  }

  function onApprove(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reason = String(new FormData(event.currentTarget).get("reason") ?? "").trim();
    approveVersion(versionId, reason)
      .then((updated) => {
        setRecipe(updated);
        setStatus("Approved. It does not run until it is activated.");
        setError(null);
      })
      .catch(fail);
  }

  function onRegression() {
    runRegression(versionId)
      .then((report) => {
        setRegression(report);
        setStatus(report.passed ? "Regression passed." : "Regression failed.");
        setError(null);
      })
      .catch(fail);
  }

  function onMove(move: LifecycleMove) {
    moveVersion(versionId, move, moveReason.trim())
      .then((updated) => {
        setRecipe(updated);
        setStatus(`Now ${updated.lifecycle_state}.`);
        setError(null);
      })
      .catch(fail);
  }

  function onRollback() {
    rollbackTo(versionId, moveReason.trim())
      .then((saved) => navigate(`/recipes/${saved.id}`))
      .catch(fail);
  }

  if (!recipe) {
    return error ? <p role="alert">Could not load: {error}</p> : <p>Loading the recipe.</p>;
  }
  const needsOwner = recipe.owner_party === OWNER_UNASSIGNED;
  const needsCase = recipe.test_case_count < 1;
  const unresolved = preview?.fields.filter((field) => !field.resolved_somewhere) ?? [];

  return (
    <section aria-labelledby="recipe-title">
      <h1 id="recipe-title">
        Recipe version {recipe.version}: {recipe.lifecycle_state}
      </h1>
      {!recipe.is_current && <p>A later version of this recipe exists.</p>}
      {error && <p role="alert">{error}</p>}
      {status && <p role="status">{status}</p>}

      <form onSubmit={onSave} aria-label="Edit recipe">
        <table aria-label="Fields">
          <thead>
            <tr>
              <th>Field</th>
              <th>Meaning</th>
              <th>Source anchor</th>
              <th>On the representative files</th>
            </tr>
          </thead>
          <tbody>
            {recipe.fields.map((field, index) => (
              <tr key={field.field_name}>
                <td>{field.field_name}</td>
                <td>
                  <input
                    aria-label={`Meaning of ${field.field_name}`}
                    name={`meaning-${index}`}
                    defaultValue={field.meaning ?? ""}
                  />
                </td>
                <td>
                  <textarea
                    aria-label={`Anchor of ${field.field_name}`}
                    name={`anchor-${index}`}
                    defaultValue={JSON.stringify(field.source_anchor_pattern)}
                  />
                </td>
                <td>
                  <PreviewCell preview={preview} name={field.field_name} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <label>
          Owner{" "}
          <input name="owner" required defaultValue={needsOwner ? "" : recipe.owner_party} />
        </label>
        <label>
          Proof limits (one per line){" "}
          <textarea name="limits" defaultValue={recipe.governance.proof_limits.join("\n")} />
        </label>
        <label>
          Known exclusions (one per line){" "}
          <textarea name="exclusions" defaultValue={recipe.governance.known_exclusions.join("\n")} />
        </label>
        <label>
          Revalidation triggers (one per line){" "}
          <textarea
            name="triggers"
            defaultValue={recipe.governance.revalidation_triggers.join("\n")}
          />
        </label>
        <label>
          Rollback path{" "}
          <input name="rollback" defaultValue={recipe.governance.rollback_path ?? ""} />
        </label>
        <label>
          Why this edit <input name="reason" required />
        </label>
        <button type="submit">Save as a new version</button>
      </form>

      <form onSubmit={onCase} aria-label="Add test case">
        <h2>Test cases ({recipe.test_case_count})</h2>
        <label>
          Kind{" "}
          <select name="kind" defaultValue="clean">
            <option value="clean">clean</option>
            <option value="edge">edge</option>
            <option value="negative">negative</option>
          </select>
        </label>
        <label>
          Description <input name="description" required />
        </label>
        <label>
          Input (JSON) <textarea name="input" defaultValue="{}" />
        </label>
        <label>
          Expected (JSON) <textarea name="expected" defaultValue="{}" />
        </label>
        <button type="submit">Add test case</button>
      </form>

      <section aria-labelledby="regression-title">
        <h2 id="regression-title">Regression</h2>
        <p>
          A test case names its file in input.source_artifact_version_id and what to read in
          expected.fields. Activation needs a passed run.
        </p>
        <button type="button" onClick={onRegression}>
          Run regression
        </button>
        {regression && (
          <ul aria-label="Regression results">
            {regression.cases.map((item) => (
              <li key={item.case_id}>
                {item.description}: {item.outcome}. {item.detail}
              </li>
            ))}
          </ul>
        )}
      </section>

      <form onSubmit={onApprove} aria-label="Approve recipe">
        <h2>Approval</h2>
        {needsOwner && <p>Approval needs a named owner: save a version with one.</p>}
        {needsCase && <p>Approval needs at least one test case.</p>}
        {unresolved.length > 0 && (
          <p>
            Approval needs every field to land on a representative file. Not found:{" "}
            {unresolved.map((field) => field.field_name).join(", ")}
          </p>
        )}
        <label>
          Reason <input name="reason" required />
        </label>
        <button
          type="submit"
          disabled={
            needsOwner ||
            needsCase ||
            unresolved.length > 0 ||
            !["proposed", "under_evaluation"].includes(recipe.lifecycle_state)
          }
        >
          Approve this version
        </button>
      </form>

      <section aria-labelledby="lifecycle-title">
        <h2 id="lifecycle-title">Lifecycle</h2>
        <label>
          Reason for the change{" "}
          <input value={moveReason} onChange={(event) => setMoveReason(event.target.value)} />
        </label>
        {(MOVES[recipe.lifecycle_state] ?? []).map(({ move, label }) => (
          <button key={move} type="button" disabled={!moveReason.trim()} onClick={() => onMove(move)}>
            {label}
          </button>
        ))}
        {!recipe.is_current && (
          <button type="button" disabled={!moveReason.trim()} onClick={onRollback}>
            Roll back to this version
          </button>
        )}
      </section>
    </section>
  );
}
